import { useCallback, useEffect, useRef, useState } from "react";
import api from "@/lib/api";

// Server-side search + "Load more" paging for the big lists (Expense Log,
// Purchase Orders, Accounting, Card Transactions). The endpoint returns
// { items, next_cursor, counts? }; counts come with the first page.
// `params` changes (search text, filters, tab) restart from page one;
// search text should be debounced by the caller (useDebounced below).
export default function usePagedSearch(url, params) {
  const [items, setItems] = useState(null);
  const [counts, setCounts] = useState(null);
  const [nextCursor, setNextCursor] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const requestId = useRef(0);
  const key = JSON.stringify(params);

  const reload = useCallback(() => {
    const id = ++requestId.current;
    const clean = Object.fromEntries(Object.entries(params).filter(([, v]) => v !== "" && v != null && v !== false));
    return api.get(url, { params: clean }).then((r) => {
      if (id !== requestId.current) return; // a newer search already started
      setItems(r.data.items);
      setNextCursor(r.data.next_cursor || null);
      if (r.data.counts) setCounts(r.data.counts);
    });
  }, [url, key]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { reload(); }, [reload]);

  const loadMore = async () => {
    if (!nextCursor) return;
    setLoadingMore(true);
    const clean = Object.fromEntries(Object.entries(params).filter(([, v]) => v !== "" && v != null && v !== false));
    try {
      const r = await api.get(url, { params: { ...clean, cursor: nextCursor } });
      setItems((prev) => [...(prev || []), ...r.data.items]);
      setNextCursor(r.data.next_cursor || null);
    } finally {
      setLoadingMore(false);
    }
  };

  return { items, counts, nextCursor, loadMore, loadingMore, reload };
}

export function useDebounced(value, ms = 300) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}
